/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/

import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';

import { LocoDetail } from './../../../core/model/loco-detail.model';
import { TrainData } from './../../../core/model/train-data.model';

/**
 * @export
 * @class LmsTrainSvgComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'lms-train-svg',
  templateUrl: './lms-train-svg.component.html',
  styleUrls: ['./lms-train-svg.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LmsTrainSvgComponent implements OnInit, OnDestroy, OnChanges {

  @Input() screenName: string;
  @Input() trainData: TrainData;
  locoDetails: Array<LocoDetail>;
  @Input() isDefaultTrainIcon: boolean;
  public outerBorderClass: string;
  public innerTextColor: string;
  public innerBackgroundColor: string;
  public trainId: string;
  public isPtcIndicator: boolean;
  public trainTonnage: number;
  public trainIdOfPlannedPowerFromTrain1: string;
  public calculatedTonnage: number; // Hardcoded values, removed once we get value from backend.
  public upsTrainText: string;

  /* ARRV – Actual Arrival Time at this location
      ETA – Estimated Time of Arrival; the adjusted scheduled arrival time at this location

      SPRJ – Saved Projected Ready Time: indicates the time the train is supposed to be ready
      at this terminal; arrival here has not been reported yet

      SCAL – Saved Call Time: the time this train (crew) is called at this terminal; on an inbound train,
      the arrival here has not been reported yet. Call times are saved if there is no power on this
      train or if the call was received more than 5 minutes before the call time

      PROJ – Projected Ready Time at this location
      Call – Crew Call time at this terminal
      DEPT – Actual Departure Time from this terminal
      SCH – Original scheduled arrival time; the train has not yet left from the origin and no new
      adjustments have been made to the schedule
      SDPT - Scheduled Departure Time
  */
  public status: string;
  public locomotiveAssignedToTrain: number;
  public trainIdOfPlannedPowerFromTrain2: string;
  public associatedStatusTime: string;
  public trainHasNotes: string;
  public outerRectHeight: string;

  private blackOuterBorder = 'bdr-black';
  private yellowOuterBorder = 'bdr-yellow';
  private redOuterBorder = 'bdr-red';
  private aquaOuterBorder = 'bdr-aqua';
  private redBackground = 'bg-red';
  private yellowBackground = 'bg-yellow';
  private greenBackground = 'bg-green';
  private whiteBackground = 'bg-white';
  private innetTextBlackColor = 'inner-text-black-color';
  public innerBackgroundWidth = '195';

  /**
   * Creates an instance of LmsTrainSvgComponent.
   * @memberOf LmsTrainSvgComponent
   */
  constructor() { }

  /**
   * This method initializes the Train icon Drawing.
   * @memberOf LmsTrainSvgComponent
   */
  ngOnInit() {
    this.initialize();
  }

  /**
   * This initializes train Icon Drawing and Setting Params used into it
   * @memberOf LmsTrainSvgComponent
   */
  initialize() {
    this.outerBorderClass = this.blackOuterBorder;
    this.innerBackgroundColor = this.whiteBackground;
    this.innerTextColor = this.innetTextBlackColor;

    if (this.screenName && this.screenName !== 'Terminal') {
      this.outerRectHeight = '195';
      this.innerBackgroundWidth = '195';
    } else {
      this.outerRectHeight = '285';
      this.innerBackgroundWidth = '295';
    }

    if (this.trainData) {
      if (this.trainData.trainSitting) {
        this.setTrainBorder(this.trainData.trainSitting);
      }
      if (this.trainData.hotTrain) {
        this.setInnerTextColor(this.trainData.hotTrain);
      }
      if (this.trainData.trainIdentifier) {
        this.trainId = this.trainData.trainIdentifier;
      } else if (this.trainData.trainName) {
        this.trainId = this.trainData.trainName;
      }
      this.isPtcIndicator = this.isNotBlank(this.trainData.ptcTrainIndicator) &&
        (this.trimUpperCaseValue(this.trainData.ptcTrainIndicator) === 'Y' ||
          this.trimUpperCaseValue(this.trainData.ptcTrainIndicator) === 'P') ? true : false;
      if (this.trainData.projectedTons) {
        this.trainTonnage = parseInt(this.trainData.projectedTons, 10);
      }
      this.trainIdOfPlannedPowerFromTrain1 = this.trainData.planFromTrain1;
      // If displayStatus is 'PROJ', use scheduledText/scheduledDateTime, else use displayStatus/displayTime
      if (this.trainData.displayStatus === 'PROJ') {
        this.status = 'SDPT';
        if (this.trainData.scheduledDateTime) {
          const parts = this.trainData.scheduledDateTime.split(',');
          this.associatedStatusTime = parts.length > 1 ? parts[1] : this.trainData.scheduledDateTime;
        } else {
          this.associatedStatusTime = '';
        }
      } else {
        this.status = this.trainData.displayStatus;
        this.associatedStatusTime = this.trainData.displayTime;
      }

      if (this.trainData.assignedUnits) {
        this.locomotiveAssignedToTrain = parseInt(this.trainData.assignedUnits, 10);
      }

      if(this.trainData.calculatedTonnage){
        this.calculatedTonnage = parseInt(this.trainData.calculatedTonnage, 10);
      }

      this.trainIdOfPlannedPowerFromTrain2 = this.trainData.planFromTrain2;
      this.trainHasNotes = (this.isNotBlank(this.trainData.trainNotes) &&
        (this.trimUpperCaseValue(this.trainData.trainNotes) === 'Y') ? 'N' : '');
      this.innerBackgroundColor = this.whiteBackground;
      if (this.trainData.compliance) {
        this.setInnerColor(this.trainData.compliance);
      }
    }
  }

  /**
   * This is used to set Train Border
   * @param {string} trainSitting
   * @memberOf LmsTrainSvgComponent
   */
  setTrainBorder(trainSitting: string) {
    if (this.trimUpperCaseValue(trainSitting) === 'R') {
      this.outerBorderClass = this.redOuterBorder;
    } else if (this.trimUpperCaseValue(trainSitting) === 'Y') {
      this.outerBorderClass = this.yellowOuterBorder;
    } else if (this.trimUpperCaseValue(trainSitting) === 'F') {
      this.outerBorderClass = this.aquaOuterBorder;
    } else {
      this.outerBorderClass = this.blackOuterBorder;
    }
  }

  /**
   * This is used to set Train inner text color
   * @param {string} hotTrain
   * @memberOf LmsTrainSvgComponent
   */
  setInnerTextColor(hotTrain: string) {
    if (this.trimUpperCaseValue(hotTrain) === 'U') {
      this.innerTextColor = this.innetTextBlackColor;
      this.upsTrainText = 'UPS';
    } else {
      this.innerTextColor = this.innetTextBlackColor;
      this.upsTrainText = '';
    }
  }

  /**
   * This is used to set Innner Color of Train
   * @param {string} compliance
   * @memberOf LmsTrainSvgComponent
   */
  setInnerColor(compliance: string) {
    if (this.trimUpperCaseValue(compliance) === 'R') {
      this.innerBackgroundColor = this.redBackground;

    } else if (this.trimUpperCaseValue(compliance) === 'Y') {
      this.innerBackgroundColor = this.yellowBackground;

    } else if (this.trimUpperCaseValue(compliance) === 'G') {
      this.innerBackgroundColor = this.greenBackground;

    } else {
      this.innerBackgroundColor = this.whiteBackground;

    }
  }

  ngOnChanges() {
    this.initialize();
  }
  /**
   * Clean state of lifecycle hook.
   * @memberOf LmsTrainSvgComponent
   */
  ngOnDestroy() {
  }

  /**
   * This is used to check whether value is blank or note
   * @param {string} value
   * @returns {boolean}
   * @memberOf LmsLocoSvgComponent
   */
  isNotBlank(value: string): boolean {
    return (value && value.trim().length > 0);
  }

  /**
   * This is used to convert into UpperCase
   * @param {string} value
   * @returns {string}
   * @memberOf LmsLocoSvgComponent
   */
  trimUpperCaseValue(value: string): string {
    return value.trim().toUpperCase();
  }
}
